/**
 * A VALIDATED PARSE → ONE STEP. Pure: never mutates the loaded entities, the open entity or the parse.
 * One entity per apply (Q5): the first entity's edits apply, the rest come back as `waiting`.
 * Guards (spec section 4): nothing applies while the field form is open, or when a different entity is
 * open than at send; switching away from unsaved changes asks first (Save / Discard / Cancel).
 * The page applies an `apply` step's `entity` as the new unsaved edits — the chat never saves (ADR 0010).
 * ⚠ `import type` only — see rules/entity-form-rules.ts.
 */
import type { Entity } from '../../../../model/entity.model';
import type { DeclineReason, EntityEdit, EntityParse, OotbInfo, RoleInfo } from '../zs-entity.types';
import { prepareNewEntityName } from '../rules/entity-form-rules';
import { entitySkeleton } from '../rules/field-builders';
import { applyFieldEdit, type ApplyContext } from '../apply/apply-edit';
import { countUnsavedChanges } from '../apply/unsaved-count';
import { actionPhrase, routeAction, type HoldStep } from './action-route';

export interface RouteContext {
  /** Every entity the page loaded, hidden included (names in use, link targets). */
  entities: readonly Entity[];
  /** The open entity's unsaved edits, or null. */
  open: Entity | null;
  /** The open entity as last saved; null when it was never saved. */
  openSaved: Entity | null;
  /** `openKey(open)` when the builder sent the sentence. */
  openKeyAtSend: string | null;
  /** The Add/Edit Field form is open. */
  fieldFormOpen: boolean;
  mint: () => string;
  nowIso: string;
  /** Plan 4b — for actions. Absent = none (older callers and tests). */
  roles?: readonly RoleInfo[];
  ootb?: readonly OotbInfo[];
  /** Plan 4b — the page's Role Permissions or Add Role modal is open. */
  permissionsOpen?: boolean;
}

export type EntityStep =
  | { kind: 'apply'; entity: Entity; opened: 'new' | 'existing' | 'open'; changes: string[]; declined: string[]; gaps: string[]; waiting: EntityEdit[] }
  | { kind: 'switch'; from: string; to: string; unsaved: number }
  | { kind: 'ask'; question: string; options: string[] }
  | { kind: 'answer'; text: string }
  | { kind: 'decline'; text: string; gaps: string[] }
  | { kind: 'blocked'; text: string }
  | { kind: 'failed'; text: string }
  // Plan 4b — a held action (import, Sync, permissions), approved in the confirm bar.
  | HoldStep;

export const DECLINE_TEXT: Record<DeclineReason, string> = {
  pages: 'That belongs to Spotlight, on the Page Builder. I only change entities and their fields.',
  data: 'I can\'t see records — only how your entities are set up.',
  delete_field: 'I can\'t delete fields yet. While an entity is New, use the ✕ next to the field on its graph.',
  delete_entity: 'Entities can\'t be deleted from this page.',
  create_role: 'Roles are created on the Roles page. Once one exists, I can give it permissions here.',
  other: 'That\'s not something I can do on the entity page.',
};
export const FAILED_TEXT = 'I couldn\'t reach the assistant, so nothing changed.';
export const FIELD_FORM_OPEN_TEXT = 'Finish or close the field form first, then send that again.';
export const ENTITY_CHANGED_TEXT = 'A different entity is open now than when you asked, so nothing changed.';
export const WHICH_ENTITY_TEXT = 'Which entity do you mean?';

/** The open entity's identity for the changed-while-thinking guard; a new, unsaved entity is ''. */
export function openKey(open: Entity | null): string | null {
  return open ? open.id : null;
}

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;
const lower = (s: string): string => s.toLowerCase();
/** Plan 4b final review (M3) — an action that rode along with an answer or a decline is named, never dropped. */
const withAction = (text: string, parse: EntityParse): string =>
  parse.action ? `${text}\n${actionPhrase(parse.action)} — ask for that on its own.` : text;

export function routeEntityParse(parse: EntityParse, ctx: RouteContext): EntityStep {
  switch (parse.kind) {
    case 'failed': return { kind: 'failed', text: FAILED_TEXT };
    // Was (2026-10-03, plan 4b final review — M3, an action riding on an answer was dropped without a word):
    // case 'answer': return { kind: 'answer', text: parse.answer ?? '' };
    case 'answer': return { kind: 'answer', text: withAction(parse.answer ?? '', parse) };
    case 'ask': return { kind: 'ask', question: parse.question ?? WHICH_ENTITY_TEXT, options: [...parse.options] };
    // Was (2026-10-03, plan 4b final review — M3, an action riding on a decline was dropped without a word):
    // case 'decline': return { kind: 'decline', text: DECLINE_TEXT[parse.decline ?? 'other'], gaps: [...parse.gaps] };
    case 'decline': return { kind: 'decline', text: withAction(DECLINE_TEXT[parse.decline ?? 'other'], parse), gaps: [...parse.gaps] };
  }
  if (ctx.fieldFormOpen) return { kind: 'blocked', text: FIELD_FORM_OPEN_TEXT };
  if (openKey(ctx.open) !== ctx.openKeyAtSend) return { kind: 'blocked', text: ENTITY_CHANGED_TEXT };

  // Plan 4b — an import, Sync or permission change is held for approval, never run here (ADR 0010).
  if (parse.kind === 'action' && parse.action) {
    return routeAction(parse.action, {
      entities: ctx.entities, open: ctx.open, openSaved: ctx.openSaved,
      unsaved: ctx.open ? countUnsavedChanges(ctx.openSaved, ctx.open) : 0,
      roles: ctx.roles ?? [], ootb: ctx.ootb ?? [], permissionsOpen: ctx.permissionsOpen ?? false,
    });
  }

  const gaps = [...parse.gaps];
  const visible = ctx.entities.filter((e) => !e.hidden);
  const open = ctx.open;
  const openUnnamedNew = !!open && !open.name && !ctx.openSaved;
  const cleaned = (n: string): string => lower(prepareNewEntityName(n, []).name);

  // "this" (entity null) = the entity created earlier in this request, else the open entity (I3).
  let lastCreated: string | null = null;
  const bound = parse.edits.map((e) => {
    const entity = e.entity ?? lastCreated;
    if (e.op === 'create_entity') lastCreated = e.entity;
    return { ...e, path: [...e.path], entity };
  });

  // Names that mean the OPEN entity: its own name — or, while it is new and still unnamed, the first entity
  // this request creates, which NAMES it (I1). A create naming the open entity means that entity.
  const OPEN = '\u0000open';
  const openNames = new Set<string>();
  if (open?.name) { openNames.add(lower(open.name)); openNames.add(cleaned(open.name)); }
  const adopt = openUnnamedNew ? bound.find((e) => e.op === 'create_entity') ?? null : null;
  if (adopt) { openNames.add(lower(adopt.entity as string)); openNames.add(cleaned(adopt.entity as string)); }
  const keyOf = (name: string | null): string | null =>
    name === null ? (open ? OPEN : null) : (openNames.has(lower(name)) || openNames.has(cleaned(name)) ? OPEN : lower(name));

  const keyed = bound.map((e) => ({ e, k: keyOf(e.entity) }));
  if (keyed.some((x) => x.k === null)) {
    return { kind: 'ask', question: WHICH_ENTITY_TEXT, options: visible.slice(0, 6).map((e) => e.name) };
  }

  // One entity per apply, in the order the entities first appear (Q5).
  const order: string[] = [];
  const groups = new Map<string, EntityEdit[]>();
  for (const { e, k } of keyed) {
    if (!groups.has(k as string)) { groups.set(k as string, []); order.push(k as string); }
    groups.get(k as string)!.push(e);
  }
  const first = groups.get(order[0])!;
  const waiting = order.slice(1).flatMap((k) => groups.get(k)!);
  const createEdit = first.find((e) => e.op === 'create_entity') ?? null;
  const unsaved = open ? countUnsavedChanges(ctx.openSaved, open) : 0;
  const fromName = open?.name || 'the new entity';

  let draft: Entity;
  let opened: 'new' | 'existing' | 'open';
  const changes: string[] = [];
  const declined: string[] = [];
  if (order[0] === OPEN) {
    draft = clone(open as Entity);
    opened = 'open';
    if (createEdit && createEdit === adopt) {
      const prepared = prepareNewEntityName(createEdit.entity as string, ctx.entities);
      if (!prepared.check.ok) {
        return { kind: 'decline', text: `Couldn't create ${createEdit.entity}: ${prepared.check.reason}`, gaps };
      }
      draft.name = prepared.name;
      changes.push(`Named the new entity ${prepared.name}`);
    }
    // Making an entity local is Keep Local — a commit the chat never makes (ADR 0010).
    // Was (2026-10-03, final review — J5, Keep Local is commented out of the page: local is chosen at
    // creation now):
    // if (createEdit?.local && draft.status !== 'LOCAL') declined.push(`To make ${draft.name || 'the new entity'} local, use Keep Local.`);
    if (createEdit?.local && draft.status !== 'LOCAL') declined.push(`${draft.name || 'The new entity'} isn't local — local is chosen when an entity is created.`);
  } else if (createEdit) {
    const prepared = prepareNewEntityName(createEdit.entity as string, ctx.entities);
    if (!prepared.check.ok) {
      return { kind: 'decline', text: `Couldn't create ${createEdit.entity}: ${prepared.check.reason}`, gaps };
    }
    if (open && unsaved > 0) return { kind: 'switch', from: fromName, to: prepared.name, unsaved };
    draft = { ...entitySkeleton(createEdit.local ? 'local' : 'database'), name: prepared.name };
    opened = 'new';
    changes.push(`Created ${draft.name}${createEdit.local ? ' (local)' : ''}`);
  } else {
    const targetName = first[0].entity as string;
    const saved = visible.find((e) => lower(e.name) === lower(targetName));
    if (!saved) return { kind: 'decline', text: `There's no entity called ${targetName}.`, gaps };
    if (open && unsaved > 0) return { kind: 'switch', from: fromName, to: saved.name, unsaved };
    draft = clone(saved);
    opened = 'existing';
  }

  const actx: ApplyContext = { entities: ctx.entities, mint: ctx.mint, nowIso: ctx.nowIso };
  for (const e of first) {
    if (e === createEdit) continue;
    if (e.op === 'create_entity') { declined.push(`${e.entity} is already being created.`); continue; }
    const out = applyFieldEdit(draft, { ...e, entity: draft.name }, actx);
    changes.push(...out.changes);
    declined.push(...out.declined);
  }
  // Plan 4b — an action that rode along with edits is named, not done: it needs its own request.
  if (parse.action) declined.push(`${actionPhrase(parse.action)} — ask for that on its own.`);
  if (!changes.length && !declined.length) {
    return { kind: 'decline', text: `${draft.name || 'The new entity'} is already open.`, gaps };
  }
  if (!changes.length) return { kind: 'decline', text: declined.join(' '), gaps };
  return { kind: 'apply', entity: draft, opened, changes, declined, gaps, waiting };
}

// Was (2026-10-03, final review — I1–I3):
//   const gaps = [...parse.gaps];
//   const visible = ctx.entities.filter((e) => !e.hidden);
//
//   // "this" (entity null) means the open entity; with nothing open, ask.
//   const named = parse.edits.map((e) => ({ ...e, path: [...e.path], entity: e.entity ?? ctx.open?.name ?? null }));
//   if (named.some((e) => !e.entity)) {
//     return { kind: 'ask', question: WHICH_ENTITY_TEXT, options: visible.slice(0, 6).map((e) => e.name) };
//   }
//
//   // One entity per apply, in the order the entities first appear (Q5).
//   const order: string[] = [];
//   const groups = new Map<string, EntityEdit[]>();
//   for (const e of named) {
//     const k = lower(e.entity as string);
//     if (!groups.has(k)) { groups.set(k, []); order.push(k); }
//     groups.get(k)!.push(e);
//   }
//   const first = groups.get(order[0])!;
//   const waiting = order.slice(1).flatMap((k) => groups.get(k)!);
//   const targetName = first[0].entity as string;
//   // Was (2026-10-03, review — a create naming the open entity made a self-switch):
//   // const create = first.find((e) => e.op === 'create_entity') ?? null;
//   const createEdit = first.find((e) => e.op === 'create_entity') ?? null;
//   // A create naming the OPEN entity means that entity (review, 2026-10-03): never a switch from X to X.
//   const namesOpen = (raw: string): boolean =>
//     !!ctx.open?.name && [raw, prepareNewEntityName(raw, []).name].some((n) => lower(n) === lower(ctx.open!.name));
//   const create = createEdit && !namesOpen(createEdit.entity as string) ? createEdit : null;
//   const unsaved = ctx.open ? countUnsavedChanges(ctx.openSaved, ctx.open) : 0;
//   const fromName = ctx.open?.name || 'the new entity';
//
//   let draft: Entity;
//   let opened: 'new' | 'existing' | 'open';
//   const changes: string[] = [];
//   if (create) {
//     const prepared = prepareNewEntityName(create.entity as string, ctx.entities);
//     if (!prepared.check.ok) {
//       return { kind: 'decline', text: `Couldn't create ${create.entity}: ${prepared.check.reason}`, gaps };
//     }
//     if (ctx.open && unsaved > 0) return { kind: 'switch', from: fromName, to: prepared.name, unsaved };
//     draft = { ...entitySkeleton(create.local ? 'local' : 'database'), name: prepared.name };
//     opened = 'new';
//     changes.push(`Created ${draft.name}${create.local ? ' (local)' : ''}`);
//   } else if (ctx.open && lower(ctx.open.name) === lower(targetName)) {
//     draft = clone(ctx.open);
//     opened = 'open';
//   } else {
//     const saved = visible.find((e) => lower(e.name) === lower(targetName));
//     if (!saved) return { kind: 'decline', text: `There's no entity called ${targetName}.`, gaps };
//     if (ctx.open && unsaved > 0) return { kind: 'switch', from: fromName, to: saved.name, unsaved };
//     draft = clone(saved);
//     opened = 'existing';
//   }
//
//   const actx: ApplyContext = { entities: ctx.entities, mint: ctx.mint, nowIso: ctx.nowIso };
//   const declined: string[] = [];
//   for (const e of first) {
//     // Was (2026-10-03, review — a create naming the open entity made a self-switch):
//     // if (e === create) continue;
//     if (e === create || e === createEdit) continue;
//     if (e.op === 'create_entity') { declined.push(`${e.entity} is already being created.`); continue; }
//     const out = applyFieldEdit(draft, { ...e, entity: draft.name }, actx);
//     changes.push(...out.changes);
//     declined.push(...out.declined);
//   }
//   if (!changes.length) return { kind: 'decline', text: declined.join(' '), gaps };
//   return { kind: 'apply', entity: draft, opened, changes, declined, gaps, waiting };
